import { html, css, LitElement, unsafeCSS } from 'lit';
import { choose } from 'lit/directives/choose.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { HTMLAttributes } from 'react';
import {
   faCircleInfo,
   faLightbulb,
   faMessageExclamation,
   faOctagonExclamation,
} from '@fortawesome/pro-duotone-svg-icons/index.js';
import { icon, dom, IconDefinition } from '@fortawesome/fontawesome-svg-core/index.js';

export const noteTypes = ['note', 'tip', 'important', 'warning', 'caution'] as const;
export type NoteType = (typeof noteTypes)[number];

export interface WikiNoteHTMLAttributes<T> extends HTMLAttributes<T> {
   type: NoteType | null;
}

declare global {
   // eslint-disable-next-line @typescript-eslint/no-namespace
   namespace JSX {
      interface IntrinsicElements {
         'wiki-note': React.DetailedHTMLProps<WikiNoteHTMLAttributes<WikiNote>, WikiNote>;
      }
   }
}

function faIcon(faImportedIcon: IconDefinition) {
   const htmls = icon(faImportedIcon, {
      attributes: { width: 16, height: 16 },
   }).html;
   return htmls.map(unsafeHTML);
}
export class WikiNote extends LitElement {
   static styles = css`
      ${unsafeCSS(dom.css())}
      :host {
         display: block;
         font: inherit;
         margin-block: 1em;
      }
      ::slotted(*) {
         margin-block: 0 !important;
      }
      .note-box {
         border-left: 4px solid currentColor;
         display: grid;
         gap: 1em;
         padding-block: 0.5em;
         padding-inline: 1em;
      }
      .type {
         color: currentColor;
         display: flex;
         align-items: center;
         text-transform: capitalize;
         font-size: 18px;
         font-weight: 700;
         white-space: normal;
      }
      .type > svg {
         margin-right: 0.5em;
      }
      .note,
      .note-box {
         color: var(--color-blue-500, #3b82f6);
      }
      .tip {
         color: var(--color-green-500, #10b981);
      }
      .important {
         color: var(--color-purple-500, #8b5cf6);
      }
      .warning {
         color: var(--color-yellow-600, #d97706);
      }
      .caution {
         color: var(--color-red-500, #ef4444);
      }
      slot {
         color: var(--color-gray-800, #1f2937);
      }
   `;

   static properties = { type: { type: String } };
   declare type: NoteType;

   constructor() {
      super();
      this.type = 'note';
   }

   render() {
      return html`
         <div class="note-box ${this.type}">
            <div class="type">
               ${choose(
                  this.type,
                  [
                     ['tip', () => faIcon(faLightbulb)],
                     ['important', () => faIcon(faMessageExclamation)],
                     ['warning', () => faIcon(faMessageExclamation)],
                     ['caution', () => faIcon(faOctagonExclamation)],
                  ],
                  () => faIcon(faCircleInfo)
               )}
               ${this.type}
            </div>
            <slot></slot>
         </div>
      `;
   }
}

customElements.define('wiki-note', WikiNote);
